import { Case, CasesService, CaseSystemTrigger } from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import Loading from '@/components/Atoms/Loading'
import { HasFeature } from '@/components/Atoms/Permissions'
import { ReadonlyButton } from '@/components/Atoms/ReadonlyButton'
import { UserStampTooltip } from '@/components/Atoms/UserStampTooltip'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Header } from '@/components/ui/header'
import { Label } from '@/components/ui/label'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { CasesFeature } from '@/lib/features'
import format from '@/lib/format'
import { buildPath } from '@/routes'
import {
  CopyIcon,
  FlameIcon,
  HandshakeIcon,
  InfoIcon,
  MessageSquareWarningIcon,
  TagIcon,
} from 'lucide-react'
import { Suspense, useState } from 'react'
import { Outlet } from 'react-router'
import { toast } from 'sonner'
import { useCase } from '../hooks/case-provider'
import CaseActions from './CaseActions'
import CaseComplainantTypeButton from './CaseComplainantTypeButton'
import { CaseDeletedSummary } from './CaseDeletedSummary'
import CaseDetailsNavigation from './CaseDetailsNavigation'
import { CaseOperationTypeButton } from './CaseOperationTypeButton'
import CaseStatusBadge from './CaseStatusBadge'
import { CaseStatusSelect } from './CaseStatusSelect'
import { CaseTopicButton } from './CaseTopicButton'
import CaseTypeButtons from './CaseTypeButtons'

export function CaseDetailsLayout() {
  // Hooks
  const { case: _case, reload } = useCase()

  // State
  const [updating, setUpdating] = useState(false)

  // Update case helper
  const updateCase = async (v: Partial<Case>) => {
    try {
      setUpdating(true)
      await CasesService.updateCase(_case.id!, v)
      toast.success('Case updated successfully')
      reload(true)
    } catch (error) {
      toast.error('Failed to update case. Please try again.')
    } finally {
      setUpdating(false)
    }
  }

  // Wrap with edit check component. A deleted case is always read-only,
  // regardless of the Edit feature, so every editing affordance falls back the same way it
  // would for a user without edit permission.
  const canEdit = (
    children: React.ReactNode,
    noFeatureComponent?: React.ReactNode
  ) => {
    if (_case.deleted) return noFeatureComponent ?? null
    return (
      <HasFeature
        feature={CasesFeature.Edit}
        noFeatureComponent={noFeatureComponent}
      >
        {children}
      </HasFeature>
    )
  }

  return (
    <>
      <Breadcrumb
        label={`#${_case.code}`}
        path={buildPath('/cases/:caseId', { caseId: String(_case.id!) })}
      />

      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex items-center gap-2">
            <Header
              variant="h1"
              className="overflow-hidden overflow-ellipsis whitespace-nowrap"
            >
              {_case.businessEntity
                ? format.CarrierName(_case.businessEntity)
                : _case.details.companyName}
            </Header>
            {_case.deleted && <Badge variant="destructive">Deleted</Badge>}
            {canEdit(
              <CaseActions case={_case} onDelete={() => reload(true)} />
            )}
          </div>
          <h2 className="text-md text-muted-foreground flex items-center gap-2 font-mono tracking-wide">
            <span className="-mr-2 select-none">#</span>
            {_case.code}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label="Copy case code"
                  onClick={() => {
                    navigator.clipboard.writeText(_case.code || '')
                    toast.success('Case code copied to clipboard')
                  }}
                >
                  <CopyIcon />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right">Copy</TooltipContent>
            </Tooltip>
          </h2>
        </div>
        <div className="flex flex-col items-start gap-1 lg:items-end">
          {canEdit(
            <CaseStatusSelect
              value={_case.status}
              onChange={(v) => updateCase({ status: v || undefined })}
              dropdownMenuContentProps={{ align: 'end' }}
              single
              primaryCaseType={_case.typePrimary}
              required
            />,
            <CaseStatusBadge status={_case.status} />
          )}

          <div className="my-1 hidden grid-cols-[minmax(0,max-content)_1fr] gap-1 gap-x-2 text-right lg:grid">
            <Label className="justify-self-end text-sm">Filed On</Label>
            <UserStampTooltip by={_case.created} />

            <Label className="justify-self-end text-sm">Last Updated</Label>
            <UserStampTooltip by={_case.modified} />
          </div>
        </div>
      </div>

      <CaseDeletedSummary case={_case} />

      {/* Mobile timestamps */}
      <div className="-mt-2 grid grid-cols-2 gap-4 md:flex lg:hidden">
        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Filed On
          </Label>
          <UserStampTooltip by={_case.created} />
        </div>

        <div>
          <Label className="text-muted-foreground text-sm lg:text-right">
            Last Updated
          </Label>
          <UserStampTooltip by={_case.modified} />
        </div>
      </div>

      <div className="flex flex-wrap items-start gap-2">
        {canEdit(
          <CaseTopicButton
            value={_case.topic || null}
            disabled={updating}
            onChange={(v) => updateCase({ topicId: v?.id || null })}
          />,
          <ReadonlyButton tooltip="Hot Topic" muted={!_case.topic?.name}>
            <FlameIcon className="stroke-orange-600" />
            {_case.topic?.name ?? 'Not Selected'}
          </ReadonlyButton>
        )}

        {canEdit(
          <CaseTypeButtons
            case={_case}
            disabled={updating}
            onChange={(v, secondary) =>
              updateCase({
                [secondary ? 'typeSecondaryId' : 'typePrimaryId']:
                  v?.id || null,
              })
            }
          />,
          <>
            <ReadonlyButton
              tooltip="Primary Case Type"
              muted={!_case.typePrimary?.name}
            >
              <TagIcon className="stroke-primary" />
              {_case.typePrimary?.name ?? 'Not Selected'}
            </ReadonlyButton>
            <ReadonlyButton
              tooltip="Secondary Case Type"
              muted={!_case.typeSecondary?.name}
            >
              <TagIcon className="stroke-primary" />
              {_case.typeSecondary?.name ?? 'Not Selected'}
            </ReadonlyButton>
          </>
        )}

        {canEdit(
          <CaseOperationTypeButton
            value={_case.operationType?.id || null}
            disabled={updating}
            onChange={(v) => updateCase({ operationTypeId: v?.id || null })}
          />,
          <ReadonlyButton
            tooltip="Operation Type"
            muted={!_case.operationType?.name}
          >
            <HandshakeIcon className="stroke-primary" />
            {_case.operationType?.name ?? 'Not Selected'}
          </ReadonlyButton>
        )}

        {canEdit(
          <CaseComplainantTypeButton
            case={_case}
            disabled={updating}
            onChange={(v) => updateCase({ complainantTypeId: v?.id || null })}
          />,
          <ReadonlyButton
            tooltip="Complainant Type"
            muted={!_case.complainantType?.name}
          >
            <MessageSquareWarningIcon className="stroke-primary" />
            {_case.complainantType?.name ?? 'Not Selected'}
          </ReadonlyButton>
        )}
      </div>

      {/* System trigger */}
      {_case.systemTrigger && (
        <Alert variant="info">
          <InfoIcon />
          <AlertTitle>System Generated Case</AlertTitle>
          <AlertDescription className="block">
            This case was automatically created by the{' '}
            <strong className="font-semibold underline">
              {format.Enum(CaseSystemTrigger, _case.systemTrigger)}
            </strong>{' '}
            system trigger.
          </AlertDescription>
        </Alert>
      )}

      <CaseDetailsNavigation case={_case} />

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </>
  )
}
